import { PrinterIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";

import useCurrentFacility from "@/pages/Facility/utils/useCurrentFacility";
import careConfig from "@careConfig";

import PrintEmailDialog from "@/CAREUI/misc/PrintEmailDialog";
import PrintFooter from "@/components/Common/PrintFooter";

import { formatName, formatPatientAge } from "@/Utils/utils";
import { ServiceRequestReadSpec } from "@/types/emr/serviceRequest/serviceRequest";
import { PatientIdentifierUse } from "@/types/patient/patientIdentifierConfig/patientIdentifierConfig";

interface PatientInstructionPrintSheetProps {
  request: ServiceRequestReadSpec;
  testName: string;
}

export function PatientInstructionPrintSheet({
  request,
  testName,
}: PatientInstructionPrintSheetProps) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);

  const { facility } = useCurrentFacility();

  const patient = request.encounter.patient;
  const orderedBy = request.requester ? formatName(request.requester) : "—";
  const orderedDate = new Date(request.created_date)
    .toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    })
    .replace(/\//g, "-");

  const appointmentDateTime = request.occurance
    ? new Date(request.occurance)
        .toLocaleString("en-IN", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        })
        .replace(/\//g, "-")
    : "—";

  const handlePrint = () => {
    setIsOpen(false);
    setIsPrinting(true);
  };

  useEffect(() => {
    if (isPrinting) {
      const timeout = setTimeout(() => {
        window.print();
        setIsPrinting(false);
      }, 200);
      return () => clearTimeout(timeout);
    }
  }, [isPrinting]);

  return (
    <>
      <Sheet open={isOpen} onOpenChange={setIsOpen}>
        <SheetTrigger asChild>
          <Button
            variant="outline"
            className="font-semibold border border-gray-400"
          >
            <PrinterIcon className="size-4" />
            {t("print_patient_instructions")}
          </Button>
        </SheetTrigger>
        <SheetContent className="w-full sm:max-w-lg">
          <SheetHeader>
            <SheetTitle>{t("patient_instruction_handout_preview")}</SheetTitle>
          </SheetHeader>

          {/* Preview */}
          <div className="mt-4 border border-gray-200 rounded-lg p-6 bg-white text-sm space-y-4">
            <div className="text-center border-b pb-3">
              <div className="text-base font-bold text-gray-900">
                AayushNet Health
              </div>
              <div className="text-lg font-semibold text-gray-800 mt-1">
                Patient Preparation Instructions
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2 text-xs">
              <div>
                <span className="text-gray-500">Patient: </span>
                <span className="font-semibold">{patient.name}</span>
              </div>
              <div>
                <span className="text-gray-500">Patient ID: </span>
                <span className="font-mono">{patient.id}</span>
              </div>
              <div>
                <span className="text-gray-500">Test: </span>
                <span className="font-semibold">{testName}</span>
              </div>
              <div>
                <span className="text-gray-500">Ordered By: </span>
                <span>{orderedBy}</span>
              </div>
              <div>
                <span className="text-gray-500">Date: </span>
                <span>{orderedDate}</span>
              </div>
              <div className="col-span-2">
                <span className="text-gray-500">Appointment: </span>
                <span className="font-semibold text-blue-600">
                  {appointmentDateTime}
                </span>
              </div>
            </div>

            {request.patient_instruction && (
              <div className="bg-amber-50 border border-amber-200 rounded p-3">
                <div className="text-xs font-semibold text-amber-800 mb-1">
                  Instructions from your doctor:
                </div>
                <p className="text-xs text-amber-900 whitespace-pre-wrap">
                  {request.patient_instruction}
                </p>
              </div>
            )}

            <div className="text-xs text-gray-500 border-t pt-3 text-center">
              Please arrive on time and follow the instructions given above.
              <br />
              Contact us if you have any questions before your appointment.
            </div>
          </div>

          <div className="mt-4 flex justify-end gap-2">
            <Button onClick={handlePrint} className="gap-1.5">
              <PrinterIcon className="size-4" />
              {t("print")}
            </Button>
            <PrintEmailDialog
              documentTitle={t("patient_instructions")}
              patientId={patient.id}
              patientEmail={patient.email}
              patientName={patient.name}
              printableElementId="patient-instruction-print"
            />
          </div>
        </SheetContent>
      </Sheet>

      {/* Print-only area — hidden on screen, shown only when printing */}
      {(isPrinting || isOpen) && (
        <div
          id="patient-instruction-print"
          className={
            isPrinting
              ? "bg-white text-black print:block p-8"
              : "fixed -left-[10000px] top-0 w-[794px] bg-white p-8 text-black"
          }
          style={{ fontFamily: "sans-serif" }}
        >
          <div className="max-w-4xl mx-auto">
            {/* Header with Facility Name and Logo */}
            <div className="flex justify-between items-start pb-2 border-b border-gray-200">
              <div className="flex items-start gap-4">
                <div className="text-left">
                  <h1 className="text-2xl font-medium">{facility?.name}</h1>
                  {facility?.address && (
                    <div className="text-gray-500 whitespace-pre-wrap wrap-break-word text-sm">
                      {facility.address}
                      {facility.phone_number && (
                        <p className="text-gray-500 text-sm">
                          {t("phone")}: {facility.phone_number}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>
              {careConfig.mainLogo?.dark && (
                <img
                  src={careConfig.mainLogo.dark}
                  alt="Care Logo"
                  className="h-10 w-auto object-contain ml-6"
                />
              )}
            </div>

            <h2 className="text-gray-500 uppercase text-sm tracking-wide font-semibold my-2">
              {t("patient_instructions")}
            </h2>

            {/* Patient Details */}
            <div className="grid md:grid-cols-2 print:grid-cols-2 gap-x-6 gap-y-1 border-t border-gray-200 pt-2 text-sm">
              <div className="grid grid-cols-[6rem_auto_1fr] items-center">
                <span className="text-gray-600">{t("patient")}</span>
                <span className="text-gray-600">:</span>
                <span className="font-semibold ml-2 wrap-break-word">
                  {patient.name}
                </span>
              </div>
              {patient.instance_identifiers
                ?.filter(
                  ({ config }) =>
                    config.config.use === PatientIdentifierUse.official,
                )
                .map((identifier) => (
                  <div
                    key={identifier.config.id}
                    className="grid grid-cols-[6rem_auto_1fr] items-center"
                  >
                    <span className="text-gray-600">
                      {identifier.config.config.display}
                    </span>
                    <span className="text-gray-600">:</span>
                    <span className="font-semibold ml-2">
                      {identifier.value}
                    </span>
                  </div>
                ))}
              <div className="grid grid-cols-[6rem_auto_1fr] items-center">
                <span className="text-gray-600">
                  {t("age")} / {t("sex")}
                </span>
                <span className="text-gray-600">:</span>
                <span className="font-semibold ml-2">
                  {formatPatientAge(patient, true)} /
                  <span className="capitalize ml-1">
                    {t(`GENDER__${patient.gender}`)}
                  </span>
                </span>
              </div>
              <div className="grid grid-cols-[6rem_auto_1fr] items-center">
                <span className="text-gray-600">Test Ordered</span>
                <span className="text-gray-600">:</span>
                <span className="font-semibold ml-2 wrap-break-word">
                  {testName}
                </span>
              </div>
              <div className="grid grid-cols-[6rem_auto_1fr] items-center">
                <span className="text-gray-600">{t("requested_by")}</span>
                <span className="text-gray-600">:</span>
                <span className="font-semibold ml-2">{orderedBy}</span>
              </div>
              <div className="grid grid-cols-[6rem_auto_1fr] items-center">
                <span className="text-gray-600">Date Ordered</span>
                <span className="text-gray-600">:</span>
                <span className="font-semibold ml-2">{orderedDate}</span>
              </div>
              <div className="grid grid-cols-[6rem_auto_1fr] items-center">
                <span className="text-gray-600">Appointment</span>
                <span className="text-gray-600">:</span>
                <span className="font-semibold ml-2">
                  {appointmentDateTime}
                </span>
              </div>
            </div>

            <div className="mt-8 space-y-8 min-h-[400px]">
              <div>
                <h2 className="text-lg font-semibold mb-2">
                  Instructions from your doctor
                </h2>
                <div className="whitespace-pre-wrap text-base text-gray-800 p-4 bg-gray-50 border border-gray-200 rounded min-h-[100px]">
                  {request.patient_instruction ||
                    "No specific instructions provided."}
                </div>
              </div>

              <div className="text-sm text-gray-500 mt-8 pt-4 border-t">
                Please arrive on time and strictly follow the preparation
                instructions above.
                <br />
                Contact the reception if you have any questions before your
                appointment.
              </div>
            </div>

            {/* Footer */}
            <PrintFooter showPrintedBy className="mt-12 pt-4 border-t" />
          </div>
        </div>
      )}
    </>
  );
}
